import React, { useMemo } from 'react';
import { Modal } from 'antd';
import type { PriceResponseDto } from '../../api-schema/generated/types';
import type { Portfolio } from '../../hooks/usePortfolios';
import ChartSection from '../GridTrading/tabs/ChartSection';
import SymbolPanel from './SymbolPanel';

interface Props {
    symbol: string | null;
    open: boolean;
    quote?: PriceResponseDto;
    portfolios: Portfolio[];
    onPlaced: () => void;
    onClose: () => void;
    mobile?: boolean;
}

/** A row's chart and order ticket together: side by side on desktop, stacked full-screen on a phone. */
const SymbolModal: React.FC<Props> = ({ symbol, open, quote, portfolios, onPlaced, onClose, mobile = false }) => {
    const instrument = useMemo(() => (symbol ? { symbol } : null), [symbol]);
    const ticket = symbol && <SymbolPanel symbol={symbol} quote={quote} portfolios={portfolios} onPlaced={onPlaced} />;
    // Same chart as the grid screen, fed the board's live quote; unmounted on close.
    const chart = <ChartSection selectedInstrument={instrument} priceData={quote} />;

    return (
        <Modal
            open={open && !!symbol}
            onCancel={onClose}
            title={symbol}
            footer={null}
            destroyOnHidden
            width={mobile ? '100vw' : 'min(1100px, 95vw)'}
            style={mobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : { top: 40 }}
            styles={{
                content: mobile ? { minHeight: '100dvh', borderRadius: 0, padding: 12 } : undefined,
                body: { background: '#1A1D23' },
            }}
        >
            {mobile ? (
                <div data-testid="symbol-modal" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* fixed box: the chart's header and axes stay inside it, never over the ticket */}
                    <div style={{ height: 340, flex: 'none', overflow: 'hidden', paddingBottom: 8, borderBottom: '1px solid #2B3139' }}>{chart}</div>
                    {ticket}
                </div>
            ) : (
                <div data-testid="symbol-modal" style={{ display: 'flex', gap: 12, height: 'min(620px, 80vh)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>{chart}</div>
                    <div style={{ width: 340, flexShrink: 0, overflowY: 'auto' }}>{ticket}</div>
                </div>
            )}
        </Modal>
    );
};

export default SymbolModal;
